/**
 * Urgencia en tabla de orden sugerido.
 * La tabla usa `replenishment_need`; las funciones BOM/ROP quedan como fallback
 * para respuestas antiguas y para el sidebar de trazas.
 */

export function isoWeekMondayFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const dow = d.getDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(y, m, day + diffToMonday);
  return monday.toISOString().slice(0, 10);
}

/** Lunes ISO de la semana que contiene (hoy local + leadDays). */
export function arrivalWeekMondayAfterLeadDays(leadDays: number): string {
  const add = Number.isFinite(Number(leadDays)) ? Number(leadDays) : 0;
  const t = new Date();
  const arrival = new Date(t.getFullYear(), t.getMonth(), t.getDate() + add);
  return isoWeekMondayFromDate(arrival);
}

export type SupplyRowUrgencyInput = {
  replenishment_need?: 'urgent' | 'needs_reorder' | 'no_need' | 'aware' | null;
  needs_reorder?: boolean;
  on_demand?: boolean;
  bom_need_week_min?: string | null;
  lead_time?: number;
  cantidad_sugerida_sistema?: number;
  reorder_point: number;
  stock_actual: number;
  stock_en_transito: number;
};

export function isSupplyTableRowNeedsReorder(row: SupplyRowUrgencyInput): boolean {
  if (row.replenishment_need != null) {
    return row.replenishment_need === 'urgent' || row.replenishment_need === 'needs_reorder';
  }
  return row.needs_reorder === true;
}

/** Productos on_demand: semana(hoy+LT) > min(week_bucket traza) y cantidad sugerida > 0. */
export function isOnDemandBomTableUrgent(row: SupplyRowUrgencyInput): boolean {
  if (!row.on_demand) return false;
  const qty = Number(row.cantidad_sugerida_sistema ?? 0);
  if (!Number.isFinite(qty) || qty <= 0) return false;
  const minWeek = row.bom_need_week_min;
  if (!minWeek) return false;
  const arrMon = arrivalWeekMondayAfterLeadDays(row.lead_time ?? 0);
  return arrMon > minWeek;
}

/** Resto: mismo criterio histórico por punto de reorden. */
export function isReorderPointTableUrgent(row: SupplyRowUrgencyInput): boolean {
  const rp = Number(row.reorder_point ?? 0);
  const suggested = Number.isFinite(Number(row.cantidad_sugerida_sistema)) && Number(row.cantidad_sugerida_sistema) > 0;
  const stock = Number(row.stock_actual ?? 0) + Number(row.stock_en_transito ?? 0);
  return stock <= rp && rp > 0 && suggested;
}

export function isSupplyTableRowUrgentRed(row: SupplyRowUrgencyInput): boolean {
  if (row.replenishment_need != null) {
    return row.replenishment_need === 'urgent';
  }
  if (row.on_demand) {
    return isOnDemandBomTableUrgent(row);
  }
  return isReorderPointTableUrgent(row);
}

/** Amarillo / chip Baja cobertura: flag backend + misma ventana que Necesitan reorden (qty > 0). */
export function isSupplyTableRowLowCoverage(
  row: {
    is_low_coverage?: boolean | null;
    cantidad_sugerida_sistema?: number | null;
    cantidad_sugerida_usuario?: number | null;
    production_scope?: { pending?: boolean } | null;
  },
  enabled: boolean,
): boolean {
  if (!enabled) return false;
  if (row.is_low_coverage !== true) return false;
  if ((row.cantidad_sugerida_sistema ?? 0) > 0) return true;
  if (row.cantidad_sugerida_usuario != null && Number(row.cantidad_sugerida_usuario) > 0) {
    return true;
  }
  return Boolean(row.production_scope?.pending);
}

/** Fila de traza en sidebar: semana(hoy+LT) > week_bucket de esa fila. */
export function isBomTraceRowUrgent(
  traceWeekBucket: string | null | undefined,
  leadTime: number
): boolean {
  if (!traceWeekBucket) return false;
  const arrMon = arrivalWeekMondayAfterLeadDays(leadTime ?? 0);
  return arrMon > traceWeekBucket;
}
